/**
 * Batch Processes Shared Invariants
 *
 * Enforces:
 * 1. Permissions - source-based command access control
 *
 * No normalize needed (simple state, no computed fields).
 * No validate needed (no form validation).
 */

import { Invariants } from '@commandDrivenStateEngine/core/invariants';
import { BatchProcessesSharedState } from './state';
import { BatchProcessesSharedCommand } from './commands';

/**
 * Permission control - source-based command access.
 *
 * Permission matrix:
 * - user: dropdown/open, dropdown/close
 * - api: subscription/* (setProcesses, upsertProcess, removeProcess),
 *        credentials/* (setRevokedChannelTargets — TV1-2446 slice 6)
 * - ai: nothing allowed
 * - url: nothing allowed
 * - application: ALL
 */
const canApply = ({
  command,
  meta
}: {
  state: BatchProcessesSharedState;
  command: BatchProcessesSharedCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // Application source: can dispatch ALL commands
  if ( source === 'application' ) {
    return true;
  }

  // API source: subscription and credential commands only
  if ( source === 'api' ) {
    return type.startsWith( 'batchProcesses/shared/subscription/' ) ||
      type.startsWith( 'batchProcesses/shared/credentials/' );
  }

  // User source: dropdown commands only
  if ( source === 'user' ) {
    return type.startsWith( 'batchProcesses/shared/dropdown/' );
  }

  // AI source: nothing allowed
  if ( source === 'ai' ) {
    return false;
  }

  // URL source: nothing allowed
  if ( source === 'url' ) {
    return false;
  }

  // Unknown sources: allow by default (don't block)
  return true;
};

export const invariants: Invariants<BatchProcessesSharedState, BatchProcessesSharedCommand> = {
  canApply
};
